import Image from "next/image";
import Link from "next/link";
import { FacebookIcon, InstagramIcon, Logo7, YoutubeIcon } from "@/components/icons/assets";

const guniWheelLinks = [
  { title: "Guni wheel 45S", href: "/products/guni-wheel-45s" },
  { title: "Guni wheel 56", href: "/products/guni-wheel-56" },
  { title: "Guni wheel 80", href: "/products/guni-wheel-80" },
  { title: "GuniHub", href: "/products/gunihub" },
];

const guniWheelXLinks = [
  { title: "Guni wheel X 56", href: "/products/guni-wheel-x-56" },
  { title: "Guni wheel X 60", href: "/products/guni-wheel-x-60" },
  { title: "Guni wheel X 80", href: "/products/guni-wheel-x-80" },
];

const quickLinks = [
  { title: "Products", href: "/products" },
  { title: "Shops", href: "/shops" },
  { title: "Installation", href: "/installation" },
  { title: "Fitment", href: "/fitment" },
  { title: "Gallery", href: "/gallery" },
  { title: "Contact", href: "/contact" },
];

const footerImages = [
  { src: "/image/footer-1.webp", alt: "GuniWheel exhibition booth with representatives presenting the product" },
  { src: "/image/footer-2.webp", alt: "GuniWheel Europe display stand with orange wheels at trade show" },
  { src: "/image/footer-3.webp", alt: "GuniWheel product demonstration at automotive trade show" },
];

const bottomLinks = [
  { title: "Cookies", href: "/cookies" },
  { title: "Privacy Policy", href: "/privacy-policy" },
  { title: "Terms & Conditions", href: "/terms-and-conditions" },
];

export default function Footer() {
  return (
    <footer
      id="footer"
      className="bg-background px-4 sm:px-6 lg:px-10 pt-[80px] md:pt-[160px] pb-[40px] xl:pb-[60px]"
    >
      <div className="w-full max-w-[1440px] mx-auto flex flex-col gap-[60px]">

        {/* ── Top Section ── */}
        <div className="flex flex-col lg:flex-row gap-8 xl:gap-[100px] h-auto xl:h-[305px]">

          {/* ── Logo + Social + SEMA ── */}
          <div className="flex flex-col gap-[32px] xl:h-[305px] rounded-[10px] flex-shrink-0">

            {/* Logo */}
            <Link href="/" className="w-full xl:w-[200px] xl:h-[34px]">
              <Logo7 className="w-full xl:w-[200px] xl:h-[34px]" />
            </Link>

            {/* Social Icons */}
            <div className="flex items-center gap-[16px] w-[140px] h-[36px]">
              <Link
                href="https://facebook.com"
                target="_blank"
                rel="noreferrer"
                aria-label="GuniWheel on Facebook"
                className="w-[36px] h-[36px] rounded-[6.55px] bg-background-50 flex items-center justify-center"
              >
                <FacebookIcon aria-hidden="true" />
              </Link>
              <Link
                href="https://instagram.com"
                target="_blank"
                rel="noreferrer"
                aria-label="GuniWheel on Instagram"
                className="w-[36px] h-[36px] rounded-[6.55px] bg-background-50 flex items-center justify-center"
              >
                <InstagramIcon aria-hidden="true" />
              </Link>
              <Link
                href="https://youtube.com"
                target="_blank"
                rel="noreferrer"
                aria-label="GuniWheel on YouTube"
                className="w-[36px] h-[36px] rounded-[6.55px] bg-background-50 flex items-center justify-center"
              >
                <YoutubeIcon aria-hidden="true" />
              </Link>
            </div>

            {/* SEMA Badge */}
            <Image
              src="/image/sema-badge.webp"
              alt="SEMA Specialty Equipment Market Association Member badge"
              width={105}
              height={171}
              className=""
            />
          </div>

          {/* ── 4 Columns ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:flex xl:justify-between gap-6 xl:gap-0 flex-1 xl:h-[238px]">

            {/* Guni Wheel */}
            <div className="flex flex-col gap-3 xl:gap-[20px] w-full xl:w-[110px] xl:h-[168px]">
              <h3 className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500 h-auto xl:h-[24px]">
                Guni Wheel
              </h3>
              <ul className="flex flex-col gap-2 xl:gap-3">
                {guniWheelLinks.map((link) => (
                  <li key={link.title}>
                    <Link
                      href={link.href}
                      className="font-roboto font-normal text-[13px] xl:text-[16px] leading-[100%] tracking-[0%] text-neutral-300 hover:text-neutral-500 transition-colors"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Guni Wheel X */}
            <div className="flex flex-col gap-3 xl:gap-[20px] w-full xl:w-[115px] xl:h-[133px]">
              <h3 className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                Guni Wheel X
              </h3>
              <ul className="flex flex-col gap-2 xl:gap-3">
                {guniWheelXLinks.map((link) => (
                  <li key={link.title}>
                    <Link
                      href={link.href}
                      className="font-roboto font-normal text-[13px] xl:text-[16px] leading-[100%] tracking-[0%] text-neutral-300 hover:text-neutral-500 transition-colors"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div className="flex flex-col gap-3 xl:gap-[20px] w-full xl:w-[82px] xl:h-[238px]">
              <h3 className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                Quick Links
              </h3>
              <ul className="flex flex-col gap-2 xl:gap-3">
                {quickLinks.map((link) => (
                  <li key={link.title}>
                    <Link
                      href={link.href}
                      className="font-roboto font-normal text-[13px] xl:text-[16px] leading-[100%] tracking-[0%] text-neutral-300 hover:text-neutral-500 transition-colors"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* GuniWheel Europe */}
            <div className="flex flex-col gap-3 xl:gap-[20px] w-full xl:w-[186px] xl:h-[128px]">
              <h3 className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                Guniwheel Europe
              </h3>
              <p className="font-roboto font-normal text-[13px] xl:text-[16px] leading-[25px] tracking-[0%] text-neutral-300">
                Parque Empresarial Campollano, Calle C, 50 •{" "}
                02007 Albacete, Spain
              </p>
            </div>

            {/* Contact */}
            <div className="flex flex-col gap-3 xl:gap-[20px] w-full xl:w-[179px] xl:h-[98px]">
              <h3 className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                Contact
              </h3>
              <div className="flex flex-col gap-2 xl:gap-3">
                <p className="font-roboto font-normal text-[13px] xl:text-[16px] leading-[100%] tracking-[0%] text-neutral-300">
                  +34 687 914 501
                </p>
                <Link
                  href="mailto:SALES@GUNIWHEEL.EU"
                  className="font-roboto font-normal text-[13px] xl:text-[16px] leading-[100%] tracking-[0%] text-neutral-300 hover:text-neutral-500 transition-colors"
                >
                  SALES@GUNIWHEEL.EU
                </Link>
              </div>
            </div>

          </div>
        </div>

        {/* ── Middle Images ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[12px] xl:h-[250px]">
          {footerImages.map((image) => (
            <div key={image.src} className="relative h-[200px] xl:h-[250px] rounded-[12px] overflow-hidden">
              <Image
                src={image.src}
                alt={image.alt}
                fill
                sizes="(max-width: 640px) 100vw, 33vw"
                className="object-cover"
              />
            </div>
          ))}
        </div>

        {/* ── Bottom Bar ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 xl:h-[16px]">

          {/* Copyright */}
          <p className="font-roboto font-normal text-[12px] sm:text-[14px] leading-[100%] tracking-[0%] text-neutral-300 xl:h-[16px]">
            Copyright © All rights reserved GUNIWHEEL, Inc 2021
          </p>

          {/* Links */}
          <div className="flex flex-wrap items-center gap-4 xl:gap-[24px] xl:h-[16px]">
            {bottomLinks.map((link) => (
              <Link
                key={link.title}
                href={link.href}
                className="font-roboto font-normal text-[12px] sm:text-[14px] leading-[100%] tracking-[0%] text-neutral-300 hover:text-neutral-500 transition-colors"
              >
                {link.title}
              </Link>
            ))}
          </div>

        </div>

      </div>
    </footer>
  );
}